import { ConsoleLogger } from '@skylab-shared/core';
import { isUndefined, MS_PER_MINUTE } from '@skylab-shared/utils';
import axios, { Axios, AxiosError } from 'axios';
import { PrinterOperationErrorResponse } from './types/operation/operation';
import { DeviceInfo, ModelInfo, OperationBeginData, OperationEndResponse } from './types/types';
import { encryptSUZ18ClientDataHeader, SUZ18Header } from './utils/header-encryption';

const btoaUTF8 = (str: string) => {
	return btoa(String.fromCharCode(...new TextEncoder().encode(str)));
}

const SUZ_BASE_URL = 'http://localhost:8000' as const;

export class Suz18Client {
	#axiosInstance: Axios;
	#logger = new ConsoleLogger(this.constructor.name);

	public readonly suzBaseUrl = SUZ_BASE_URL;

	readonly #urls = {
		deviceInfo: (deviceName: string) => `/Devices/device/${btoaUTF8(deviceName)}` as const,
		devicesNames: `/Devices/devicesNames`,

		models: `/Models/supportedModels`,
		modelInfo: (modelName: string) => `/Models/supportedModel/${btoaUTF8(modelName)}` as const,

		operationBegin: `/Operations/begin`,
		operationEnd: `/Operations/end`,
	} as const;

	private constructor(axiosInstance: Axios) {
		this.#axiosInstance = axiosInstance;
	}

	public static async createAsync(params: SUZ18Header): Promise<Suz18Client> {
		const clientData = await encryptSUZ18ClientDataHeader(params);
		const axiosInstance = axios.create({
			baseURL: SUZ_BASE_URL,
			timeout: MS_PER_MINUTE,
			headers: {
				'Content-Type': 'application/json;charset=UTF-8',
				'Accept': 'application/json',
				'Access-Control-Allow-Origin': '*',
				'Cache-Control': 'must-revalidate, no-store, no-cache, private',
				'ClientData': clientData,
			},
		});
		return new Suz18Client(axiosInstance);
	}

	public async getDeviceInfoAsync(deviceName: string): Promise<DeviceInfo | undefined> {
		try {
			const res = await this.#axiosInstance.get(this.#urls.deviceInfo(deviceName));
			return res.data;
		} catch(err) {
			this.#logger.error(err);
		}

		return undefined;
	}

	public async getExistingDevicesNamesAsync(): Promise<string[] | PrinterOperationErrorResponse> {
		try {
			const res = await this.#axiosInstance.get(this.#urls.devicesNames);
			return res.data;
		} catch(err) {
			this.#logger.error(err);

			const error: AxiosError = err as AxiosError;
			return error?.response?.data as PrinterOperationErrorResponse;
		}
	}

	public async getModelsAsync(): Promise<ModelInfo[] | undefined> {
		try {
			const res = await this.#axiosInstance.get(this.#urls.models);
			return res.data;
		} catch(err) {
			this.#logger.error(err);
		}

		return undefined;
	}

	public async getModelInfoAsync(modelName: string): Promise<ModelInfo | undefined> {
		try {
			const res = await this.#axiosInstance.get(this.#urls.modelInfo(modelName));
			return res.data;
		} catch(err) {
			this.#logger.error(err);
		}

		return undefined;
	}

	public async runPrinterOperationAsync<TResult = any>(data: OperationBeginData): Promise<OperationEndResponse<TResult> | PrinterOperationErrorResponse | undefined | null> {
		try {
			await this.#axiosInstance.post(this.#urls.operationBegin, data);
		} catch (err) {
			const error = err as AxiosError;
			return await this.handleSuzOperationHttpErrorAsync(error, data.DeviceName);
		}

		try {
			return await this.endPrinterOperationAsync(data.DeviceName);
		} catch (err) {
			this.#logger.error(err);
		}

		return undefined;
	};

	private async endPrinterOperationAsync<TResult>(deviceName: string): Promise<OperationEndResponse<TResult> | PrinterOperationErrorResponse | undefined> {
		try {
			const response = await this.#axiosInstance.post(this.#urls.operationEnd, {deviceName});
			const data = response.data;
			if (data) {
				return data;
			} else {
				await this.endPrinterOperationAsync(deviceName);
			}
		} catch (err) {
			const error = err as AxiosError;
			await this.handleSuzOperationHttpErrorAsync(error, deviceName);
		}

		return undefined;
	}

	private async handleSuzOperationHttpErrorAsync<TResult>(data: AxiosError<unknown>, deviceName: string): Promise<OperationEndResponse<TResult> | PrinterOperationErrorResponse | null | undefined> {
		const error = data as AxiosError<PrinterOperationErrorResponse>;
		if (error?.response && !isUndefined(error.response.data) && error.response.status !== -1) {
			if (error.response.data == null) {
				return await this.endPrinterOperationAsync(deviceName);
			} else {
				return error.response.data;
			}
		} else {
			return null;
		}
	}
}
